import { Button, InputPostalCode, InputText } from "@sellar-io/components";
import { CountryTypeZ } from "@sellar-io/common";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { FormField } from "../onboarding/components/FormField";
import { CountrySelectField } from "../onboarding/components/CountrySelectField";

const RequiredCountrySchema = CountryTypeZ.or(z.literal(""))
  .refine((country) => country !== "", "Country is required")
  .pipe(CountryTypeZ);

const RyftBankAccountFormSchema = z.object({
  sortCode: z.string().trim().min(1, "Sort code is required"),
  accountNumber: z.string().trim().min(1, "Account number is required"),
  accountHolderAddress: z.object({
    lineOne: z.string().trim().min(1, "Address line is required"),
    city: z.string().trim().min(1, "City is required"),
    country: RequiredCountrySchema,
    postalCode: z.string().trim().min(1, "Postal code is required"),
  }),
});

export type RyftBankAccountFormValues = z.input<
  typeof RyftBankAccountFormSchema
>;

export type RyftBankAccountSubmissionValues = z.output<
  typeof RyftBankAccountFormSchema
>;

export const RyftEditBankAccountForm = ({
  mode,
  initialData,
  isSubmitting,
  disabled = false,
  onSubmit,
}: {
  mode: "create" | "update";
  initialData: RyftBankAccountFormValues;
  isSubmitting: boolean;
  disabled?: boolean;
  onSubmit: (values: RyftBankAccountSubmissionValues) => void | Promise<void>;
}) => {
  const form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: RyftBankAccountFormSchema,
    },
    onSubmit: ({ value }) => {
      if (disabled || isSubmitting) return;
      return onSubmit(RyftBankAccountFormSchema.parse(value));
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <fieldset
        className="flex flex-col gap-4 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={isSubmitting || disabled}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="sortCode">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Sort code"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  required
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="accountNumber">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Account number"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  required
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <div className="border-t border-zinc-200 pt-2">
          <h3 className="font-semibold text-zinc-900">
            Account holder address
          </h3>
          <p className="mt-1 text-sm text-zinc-600">
            Enter the address registered to this bank account.
          </p>
        </div>

        <form.Field name="accountHolderAddress.lineOne">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Address line"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputText
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="address-line1"
                required
              />
            </FormField>
          )}
        </form.Field>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="accountHolderAddress.city">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="City"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="address-level2"
                  required
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="accountHolderAddress.country">
            {({ state, handleBlur, handleChange }) => (
              <CountrySelectField
                label="Country"
                value={state.value}
                errorMessage={state.meta.errors[0]?.message}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            )}
          </form.Field>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Subscribe
            selector={(state) => state.values.accountHolderAddress.country}
          >
            {(country) => (
              <form.Field name="accountHolderAddress.postalCode">
                {({ state, handleBlur, handleChange }) => (
                  <FormField
                    label="Postal code"
                    errorMessage={state.meta.errors[0]?.message}
                  >
                    <InputPostalCode
                      value={state.value}
                      onBlur={handleBlur}
                      onChange={(event) => handleChange(event.target.value)}
                      country={country || undefined}
                      required
                    />
                  </FormField>
                )}
              </form.Field>
            )}
          </form.Subscribe>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isSubmitting || disabled}
            loading={isSubmitting}
          >
            {mode === "create" ? "Add bank account" : "Save changes"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};
